import { useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import {
  ArrowLeft,
  Mail,
  MapPin,
  Phone,
  MessageCircle,
  ExternalLink,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import ContactHelper from '../utils/API/BackendHelper/contact';

const CONTACT_EMAIL = 'info@gyansathi.com';
const CONTACT_PHONE = '+91 8791031516';
const CONTACT_ADDRESS = 'SF 66A, Somdutt City, A Pocket, Meerut';
const MAP_URL = 'https://www.google.com/maps?q=28.946156,77.754158';
const MAP_EMBED_URL =
  'https://www.google.com/maps?q=28.946156,77.754158&hl=en&z=16&output=embed';

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  college_name: '',
  semester: '',
  course: '',
  message: '',
};

const ContactUsPage = () => {
  const router = useRouter();
  const [form, setForm] = useState(INITIAL_FORM);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // status: { type: 'success' | 'error', message: string } | null
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim() || form.name.trim().length < 2) {
      next.name = 'Please enter your name (at least 2 characters).';
    }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = 'Please enter a valid email address.';
    }
    if (form.phone.trim() && (form.phone.trim().length < 7 || form.phone.trim().length > 20)) {
      next.phone = 'Please enter a valid phone number.';
    }
    if (!form.message.trim() || form.message.trim().length < 10) {
      next.message = 'Your message must be at least 10 characters.';
    }
    if (!agreedToTerms) {
      next.privacy = 'You must accept the Privacy Policy to submit.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const res = await ContactHelper.submit({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        college_name: form.college_name.trim(),
        semester: form.semester.trim(),
        course: form.course.trim(),
        message: form.message.trim(),
        privacyAccepted: agreedToTerms,
      });

      if (res.success) {
        setStatus({
          type: 'success',
          message: res.message || "Thank you for reaching out! We'll get back to you soon.",
        });
        setForm(INITIAL_FORM);
        setAgreedToTerms(false);
      } else {
        setStatus({
          type: 'error',
          message: res.message || 'Something went wrong. Please try again.',
        });
      }
    } catch (err) {
      setStatus({
        type: 'error',
        message: 'Unable to send your message right now. Please try again later.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    'w-full rounded-xl border-2 px-4 py-3 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-pastel-blue-dark/40 transition-colors';

  return (
    <div className="min-h-screen bg-pastel-cream">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button
              onClick={() => router.push('/')}
              className="flex items-center space-x-3"
            >
              <img
                src="/assets/logo-icon.png"
                alt="Gyan Sathi Logo"
                className="h-12 w-auto"
              />
              <div className="text-left">
                <span className="text-2xl font-bold text-pastel-navy">GYAN SATHI</span>
                <p className="text-xs text-gray-600 -mt-0.5">Smart Learning Platform</p>
              </div>
            </button>
            <button
              onClick={() => router.push('/')}
              className="hidden sm:inline-flex items-center space-x-2 px-5 py-2.5 bg-white border-2 border-pastel-blue-dark text-pastel-navy rounded-full hover:bg-pastel-blue/10 transition-all duration-300 text-sm font-semibold shadow-soft"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-pastel-mint/30 rounded-full blur-3xl" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-pastel-blue/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-pastel-peach/30 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm border-2 border-pastel-yellow rounded-full px-5 py-2.5 shadow-soft mb-6">
            <MessageCircle className="w-4 h-4 text-pastel-peach-dark" />
            <span className="text-sm font-semibold text-pastel-navy">We&apos;d love to hear from you</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-pastel-navy mb-4 leading-tight">
            Contact Us
          </h1>
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="h-1.5 w-20 bg-pastel-rose rounded-full" />
            <div className="h-1.5 w-12 bg-pastel-yellow rounded-full" />
            <div className="h-1.5 w-8 bg-pastel-mint rounded-full" />
          </div>
          <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
            Have a question, feedback, or need help with your learning journey? Reach out to the
            Gyansathi team — we&apos;re happy to assist.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-3xl shadow-soft-xl border border-gray-100 p-6 sm:p-8">
                <h2 className="text-2xl font-bold text-pastel-navy mb-1">Send us a message</h2>
                <p className="text-gray-600 text-sm mb-6">
                  Fill out the form and our team will get back to you shortly.
                </p>

                {status && (
                  <div
                    role="status"
                    className={`flex items-start space-x-3 rounded-2xl p-4 mb-6 border-2 ${
                      status.type === 'success'
                        ? 'bg-pastel-mint/20 border-pastel-mint text-green-800'
                        : 'bg-pastel-rose/20 border-pastel-rose text-red-800'
                    }`}
                  >
                    {status.type === 'success' ? (
                      <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium">{status.message}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-pastel-navy mb-1.5">
                        Name <span className="text-pastel-rose">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className={`${inputBase} ${errors.name ? 'border-red-300' : 'border-gray-200'}`}
                      />
                      {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-pastel-navy mb-1.5">
                        Email <span className="text-pastel-rose">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        className={`${inputBase} ${errors.email ? 'border-red-300' : 'border-gray-200'}`}
                      />
                      {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-pastel-navy mb-1.5">
                        Phone <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+91 9XXXXXXXXX"
                        className={`${inputBase} ${errors.phone ? 'border-red-300' : 'border-gray-200'}`}
                      />
                      {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                    </div>

                    {/* Subject */}
                    <div>
                      <label htmlFor="subject" className="block text-sm font-semibold text-pastel-navy mb-1.5">
                        Subject <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        id="subject"
                        name="subject"
                        type="text"
                        value={form.subject}
                        onChange={handleChange}
                        placeholder="What is this about?"
                        className={`${inputBase} border-gray-200`}
                      />
                    </div>
                  </div>

                  {/* College Name */}
                  <div>
                    <label htmlFor="college_name" className="block text-sm font-semibold text-pastel-navy mb-1.5">
                      College Name <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      id="college_name"
                      name="college_name"
                      type="text"
                      value={form.college_name}
                      onChange={handleChange}
                      placeholder="Your college or institution name"
                      className={`${inputBase} border-gray-200`}
                    />
                  </div>

                  {/* Semester & Course */}
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="semester" className="block text-sm font-semibold text-pastel-navy mb-1.5">
                        Semester <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        id="semester"
                        name="semester"
                        type="text"
                        value={form.semester}
                        onChange={handleChange}
                        placeholder="e.g. 3rd Semester"
                        className={`${inputBase} border-gray-200`}
                      />
                    </div>
                    <div>
                      <label htmlFor="course" className="block text-sm font-semibold text-pastel-navy mb-1.5">
                        Course <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        id="course"
                        name="course"
                        type="text"
                        value={form.course}
                        onChange={handleChange}
                        placeholder="e.g. B.Tech, MBA"
                        className={`${inputBase} border-gray-200`}
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-pastel-navy mb-1.5">
                      Message <span className="text-pastel-rose">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      className={`${inputBase} resize-y ${errors.message ? 'border-red-300' : 'border-gray-200'}`}
                    />
                    {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
                  </div>

                  {/* Privacy consent */}
                  <div>
                    <label
                      htmlFor="agreedToTerms"
                      className="flex items-start space-x-3 cursor-pointer select-none"
                    >
                      <input
                        id="agreedToTerms"
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => {
                          setAgreedToTerms(e.target.checked);
                          if (errors.privacy) setErrors((prev) => ({ ...prev, privacy: undefined }));
                        }}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-pastel-blue-dark focus:ring-pastel-blue-dark"
                      />
                      <span className="text-sm text-gray-700 leading-snug">
                        I agree to the{' '}
                        <Link
                          href="/privacy-policy"
                          target="_blank"
                          className="text-pastel-blue-dark font-semibold hover:underline"
                        >
                          Privacy Policy
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="/terms-and-conditions"
                          target="_blank"
                          className="text-pastel-blue-dark font-semibold hover:underline"
                        >
                          Terms &amp; Conditions
                        </Link>
                        . <span className="text-pastel-rose">*</span>
                      </span>
                    </label>
                    {errors.privacy && <p className="mt-1 text-xs text-red-600">{errors.privacy}</p>}
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-pastel-blue-dark text-white rounded-full font-semibold shadow-soft hover:shadow-soft-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Email */}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="group block bg-white rounded-3xl shadow-soft-xl border border-gray-100 p-6 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-pastel-blue/30 rounded-2xl flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-pastel-blue-dark" />
                </div>
                <h3 className="text-lg font-bold text-pastel-navy mb-1">Email Us</h3>
                <p className="text-gray-600 text-sm mb-2">
                  For general inquiries, support, partnerships, or feedback.
                </p>
                <span className="inline-flex items-center text-pastel-blue-dark font-semibold text-sm group-hover:underline">
                  {CONTACT_EMAIL}
                </span>
              </a>

              {/* Phone */}
              <a
                href={`tel:${CONTACT_PHONE.replace(/\s+/g, '')}`}
                className="group block bg-white rounded-3xl shadow-soft-xl border border-gray-100 p-6 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-pastel-mint/40 rounded-2xl flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-pastel-blue-dark" />
                </div>
                <h3 className="text-lg font-bold text-pastel-navy mb-1">Call Us</h3>
                <p className="text-gray-600 text-sm mb-2">
                  Speak with our team during business hours for direct assistance.
                </p>
                <span className="inline-flex items-center text-pastel-blue-dark font-semibold text-sm group-hover:underline">
                  {CONTACT_PHONE}
                </span>
              </a>

              {/* Address */}
              <div className="bg-white rounded-3xl shadow-soft-xl border border-gray-100 p-6">
                <div className="w-12 h-12 bg-pastel-rose/40 rounded-2xl flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-pastel-blue-dark" />
                </div>
                <h3 className="text-lg font-bold text-pastel-navy mb-1">Visit Us</h3>
                <p className="text-gray-600 text-sm mb-3">{CONTACT_ADDRESS}</p>
                <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-soft mb-3">
                  <iframe
                    title="Gyansathi office location"
                    src={MAP_EMBED_URL}
                    width="100%"
                    height="180"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
                <a
                  href={MAP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-pastel-blue-dark text-white rounded-full hover:shadow-soft-xl transition-all duration-300 text-sm font-semibold"
                >
                  <span>Open in Google Maps</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Footer links card */}
          <div className="mt-10 bg-pastel-cream rounded-2xl p-6 sm:p-8 border-2 border-pastel-blue/30 text-center">
            <p className="text-gray-700">
              You can also review our{' '}
              <Link
                href="/privacy-policy"
                className="text-pastel-blue-dark font-semibold hover:underline"
              >
                Privacy Policy
              </Link>{' '}
              and{' '}
              <Link
                href="/terms-and-conditions"
                className="text-pastel-blue-dark font-semibold hover:underline"
              >
                Terms &amp; Conditions
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-pastel-navy text-white py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img
              src="/assets/logo-icon.png"
              alt="Gyan Sathi Logo"
              className="h-10 w-auto"
            />
            <span className="text-xl font-bold">GYAN SATHI</span>
          </div>
          <p className="text-white/60 text-sm">
            &copy; 2026 Gyansathi. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ContactUsPage;
